<template>
  <k-video-area ref="area" :items="items" :groups="groups" :saved-layout="layout" :max-entries="1000" @layout-change="on_layout_change" @group-title="rename_group" @group-color="set_group_color" @group-remove="remove_group" @group-removed="remove_group_videos">
    <template #default="{ item }">
      <instance-item v-if="item.type == 'instance'" :data="item"></instance-item>
      <item v-else :data="item" :timestamp="ts" :auto-start="!!autostart[item.id]" @tools-change="set_tool_state(item.id, $event)"></item>
    </template>
    <!-- Tools of all videos in the group; instance tiles have none -->
    <template #group-buttons="{ keys }">
      <k-menu right offset-y :close-on-content-click="false">
        <template #activator>
          <k-btn
            icon
            no-margin
            size="xs"
            icon-name="more_vert"
            :disabled="!video_keys(keys).length"
            :class="{'monitor2-active': any_tool_on(keys)}"
            v-tooltip.bottom="'Tools for all videos'"
          ></k-btn>
        </template>
        <template #default>
          <div class="monitor2-group-tools">
            <div
              v-for="tool in tools"
              :key="tool.value"
              class="monitor2-group-tool"
              :class="{'monitor2-unsupported': !tool.supported}"
              v-tooltip.left="tool.supported ? '' : unsupported_text"
            >
              <k-btn
                list
                flat
                no-margin
                size="s"
                :disabled="!tool.supported"
                :class="{'monitor2-active': all_tool_on(keys, tool.value)}"
                @click="toggle_group_tool(keys, tool.value)"
              >
                <k-icon small inherit class="mr-2">{{tool.icon}}</k-icon>{{tool.title}}
              </k-btn>
            </div>
          </div>
        </template>
      </k-menu>
    </template>
  </k-video-area>
</template>

<script>
import ChannelModel from '@/models/channel.js';
import { monitor2Items, monitor2Groups, parseMonitor2Key, toggleMonitor2Items, renameMonitor2Group, setMonitor2GroupColor, removeMonitor2Group, monitor2Layout, setMonitor2Layout, pruneMonitor2Tools } from '@/models/monitor.js';
import eventsMixin from './events.js';
import { toolSupported, SAFARI_UNSUPPORTED_TEXT } from '@/lib/browser.js';
import Item from './Item'
import InstanceItem from './InstanceItem'

// Layout changes come in bursts (dragging, resizing), so they are saved after a short pause
const LAYOUT_SAVE_DELAY = 800;

export default {
  mixins: [eventsMixin],
  components: {
    Item,
    InstanceItem
  },
  computed: {
    selected () {
      return monitor2Items(this.$store.state.account);
    },
    groups () {
      return monitor2Groups(this.$store.state.account);
    },
    // Saved in the account; KVideoArea reports changes with "layout-change"
    layout () {
      return monitor2Layout(this.$store.state.account);
    },
    events_by_uuid () {
      return _.keyBy(this.events, "uuid");
    },
    item_types () {
      return _.mapValues(_.keyBy(this.items, "id"), "type");
    },
    items () {
      var res = [];
      this.selected.forEach((key) => {
        var parsed = parseMonitor2Key(key);
        if (parsed.type == "channel") {
          var channel = ChannelModel.channel_by_uuid(parsed.uuid);
          if (channel) res.push({id: key, type: "channel", channel: channel, alternative: parsed.alternative});
        } else if (parsed.type == "event") {
          var event = this.events_by_uuid[parsed.uuid];
          if (event) res.push({id: key, type: "event", view: parsed.view, event: event});
        } else if (parsed.type == "instance") {
          var instance = this.$store.state.instances[parsed.id];
          // Instance stats aren't video, so their tiles can have any size
          if (instance) res.push({id: key, type: "instance", instance: instance, free_size: true});
        }
      });
      return res;
    }
  },
  watch: {
    // Videos added from the sidebar go to the first free spot, not where they were before.
    // Saved layout data of removed videos and groups is dropped.
    selected (keys, old) {
      if (!old || !this.$refs.area) return;
      var added = keys.filter(k => !old.includes(k));
      // Videos added during this page load start playing by themselves
      added.forEach(k => this.$set(this.autostart, k, true));
      old.forEach(k => { if (!keys.includes(k)) this.$delete(this.autostart, k); });
      // Tool selections of removed tiles are removed with them (toggleMonitor2Items)
      if (old.some(k => !keys.includes(k))) this.$refs.area.prune_layout(keys);
      if (added.length) this.$refs.area.place_as_new(added);
    }
  },
  mounted () {
    // Cleans up data left by videos and groups removed elsewhere (e.g. on another device)
    this.$refs.area.prune_layout(this.selected);
    var account = this.$store.state.account;
    if (account) {
      var acc = _.cloneDeep(account);
      if (pruneMonitor2Tools(acc, this.selected)) this.$store.dispatch("update_profile_optimistic", acc);
    }
    this.$root.$on("monitor2:play-all", this.play_all);
  },
  beforeDestroy () {
    this.$root.$off("monitor2:play-all", this.play_all);
    this.save_layout_now();
  },
  data () {
    return {
      autostart: {},
      // Tools that are on per video key, reported by the tiles
      tool_states: {},
      tools: [
        {value: "diamond", title: "Diamond display", icon: "diamond"},
        {value: "frame_monitor", title: "Freeze / black frames", icon: "monitor_heart"},
        {value: "latency", title: "Latency (MISB timestamp)", icon: "timer"},
        {value: "audio_meters", title: "Audio meters", icon: "equalizer"},
        {value: "lufs", title: "Loudness (LUFS)", icon: "graphic_eq"}
      ].map(tool => Object.assign(tool, {supported: toolSupported(tool.value)})),
      unsupported_text: SAFARI_UNSUPPORTED_TEXT
    }
  },
  methods: {
    on_layout_change (layout) {
      this._pending_layout = layout;
      clearTimeout(this._layout_timer);
      this._layout_timer = setTimeout(() => this.save_layout_now(), LAYOUT_SAVE_DELAY);
    },
    save_layout_now () {
      clearTimeout(this._layout_timer);
      var layout = this._pending_layout;
      this._pending_layout = null;
      var account = this.$store.state.account;
      if (!layout || !account || _.isEqual(monitor2Layout(account), layout)) return;
      this.$store.dispatch("update_profile_optimistic", setMonitor2Layout(_.cloneDeep(account), layout));
    },
    set_tool_state (key, state) {
      if (state) this.$set(this.tool_states, key, state);
      else this.$delete(this.tool_states, key);
    },
    video_keys (keys) {
      var types = this.item_types;
      return keys.filter(k => types[k] && types[k] != "instance");
    },
    all_tool_on (keys, tool) {
      var videos = this.video_keys(keys);
      return videos.length > 0 && videos.every(k => _.get(this.tool_states, [k, tool], false));
    },
    any_tool_on (keys) {
      return this.video_keys(keys).some(k => _.some(this.tool_states[k]));
    },
    // Turns the tool on in every video of the group, or off when it is on in all of them
    toggle_group_tool (keys, tool) {
      var videos = this.video_keys(keys);
      if (!videos.length) return;
      this.$root.$emit("monitor2:set-tools", {keys: videos, tools: {[tool]: !this.all_tool_on(keys, tool)}});
    },
    // Starts every player that isn't playing yet (sent by the sidebar's play button)
    play_all () {
      var players = [];
      var collect = (vm) => {
        vm.$children.forEach((child) => {
          if (child.$options.name === "KVideoPlayer") players.push(child);
          else collect(child);
        });
      };
      collect(this);
      players.forEach((player) => player.start());
    },
    rename_group ({id, title}) {
      var acc = _.cloneDeep(this.$store.state.account);
      this.$store.dispatch("update_profile_optimistic", renameMonitor2Group(acc, id, title));
    },
    set_group_color ({id, color}) {
      var acc = _.cloneDeep(this.$store.state.account);
      this.$store.dispatch("update_profile_optimistic", setMonitor2GroupColor(acc, id, color));
    },
    // Removes the group and the videos inside it
    remove_group (id, keys) {
      var acc = _.cloneDeep(this.$store.state.account);
      toggleMonitor2Items(acc, keys || [], false);
      this.$store.dispatch("update_profile_optimistic", removeMonitor2Group(acc, id));
    },
    // A group was removed elsewhere (e.g. the event group button in the sidebar): its videos go with it
    remove_group_videos (id, keys) {
      var acc = _.cloneDeep(this.$store.state.account);
      this.$store.dispatch("update_profile_optimistic", toggleMonitor2Items(acc, keys, false));
    },
    // Removes events (and their event groups) that are no longer in today's event list
    on_events_loaded () {
      var stale = this.selected.filter((key) => {
        var parsed = parseMonitor2Key(key);
        return parsed.type == "event" && !this.events_by_uuid[parsed.uuid];
      });
      var stale_groups = this.groups.filter((g) => g.event && !this.events_by_uuid[g.event]);
      if (!stale.length && !stale_groups.length) return;
      var acc = _.cloneDeep(this.$store.state.account);
      toggleMonitor2Items(acc, stale, false);
      stale_groups.forEach((g) => removeMonitor2Group(acc, g.id));
      this.$store.dispatch("update_profile_optimistic", acc);
    }
  }
}
</script>

<style scoped>
/* The theme has no accent color for icon buttons; the icon inherits the button color */
.monitor2-active {
  color: var(--ks-col-accent);
}
.monitor2-group-tools {
  display: flex;
  flex-direction: column;
  padding: 4px 0;
}
.monitor2-group-tool {
  display: flex;
  flex-direction: column;
}
/* Disabled buttons get no mouse events, so the tooltip is on the wrapper */
.monitor2-unsupported .ks-btn {
  pointer-events: none;
}
</style>
